import { Plugin } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';

import { KeyboardEventCode } from '@core/common-legacy/keyboard';
import type { EmojiItem, EmojiListNavigationKeys } from '@core/symbol-data';
import { EMOJI_SHORTCODE_MAP } from '@core/symbol-data';

import { EmojiServiceInternal } from './emoji-service-internal';
import { EmojiServicePublic } from './emoji-service-public';
import { OldEmoticonEmojiMap } from './old-emoticons';
import type { EmojiEditorPluginEvents, EmojiListController } from './types';
import { EditorMode } from '../../editor/types';
import type { EditorButton, RtPMPlugin } from '../../editor/types';

const emojiShortNameRegex = /(^:|\s:)(?!.*\1).*/;

export class EmojiEditorPlugin implements RtPMPlugin {
  public api: {
    emojiService: EmojiServicePublic;
  };
  public buttons = {
    toggleEmojiList: <EditorButton>{
      icon: 'insert-emoticon',
      tooltipText: 'wysiwyg.emoji',
      isActive: () => this.emojiListController.isOpen(),
      run: (state, dispatch, origin) => {
        this.api.emojiService.emojiWithSearchOpen(origin);
      },
    },
  };
  public eventStream$ = new Subject<EmojiEditorPluginEvents>();

  private editorView: EditorView;
  private shortcutRegexEmojiMap: Array<[RegExp, EmojiItem]> = Object.entries(
    OldEmoticonEmojiMap,
  ).map(([shortcut, emoji]) => {
    const escaped = shortcut.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`(^|\\s)(${escaped})$`);

    return [pattern, emoji];
  });

  private emojiServiceInternal: EmojiServiceInternal;
  private destroy$ = new Subject<void>();

  private emoticonsReplacePlugin = new Plugin({
    props: {
      handleKeyDown: (view: EditorView, { code }) => {
        if (KeyboardEventCode.Enter === code || KeyboardEventCode.Space === code) {
          this.shortcutRegexEmojiMap.some(([regex, emoji]) => {
            const { tr, schema, selection } = view.state;
            const text = selection.$to.parent.textBetween(
              0,
              selection.$to.parentOffset,
              '\n',
              '\n',
            );
            const match = text.match(regex);
            if (!match) return false;

            const start = selection.to - match[2].length;
            const emojiNode = schema.text(emoji.symbol);
            view.dispatch(tr.replaceWith(start, selection.to, emojiNode));
            return true;
          });
        }

        return false;
      },
    },
  });

  private emojiPlugin = new Plugin({
    props: {
      handleDOMEvents: {
        keyup: (view, event) => {
          const { state } = view;
          const { $to } = state.selection;
          const text = $to.parent.textBetween(0, $to.parentOffset, '\n', '');
          const match = emojiShortNameRegex.exec(text);
          this.emojiServiceInternal.emojiMatchHandler(
            view.state,
            match,
            match ? $to.pos - match[0].length : 0,
            match ? $to.pos : 0,
          );

          return false;
        },
      },
      handleKeyDown: (view, event) => {
        const { code } = event;
        const keyCodeNavigate = [
          KeyboardEventCode.Enter,
          KeyboardEventCode.ArrowUp,
          KeyboardEventCode.ArrowDown,
          KeyboardEventCode.ArrowRight,
          KeyboardEventCode.ArrowLeft,
        ].includes(<KeyboardEventCode>code);

        if (keyCodeNavigate && this.emojiListController.isOpen()) {
          this.emojiListController.handleKey(<EmojiListNavigationKeys>code);
          return true;
        }

        return false;
      },
    },
    appendTransaction: (transactions, oldState, newState) => {
      const matches: Array<{ pos: number; execArray: RegExpExecArray }> = [];
      newState.doc.descendants((node, pos, parent) => {
        if (node.isText) {
          const regex = /:(.*?):/g;
          let execArray = null;
          while ((execArray = regex.exec(node.text!))) {
            matches.push({ pos, execArray });
          }
          return false;
        }

        return true;
      });

      if (!matches.length) return null;

      const { tr } = newState;
      matches.forEach(({ pos, execArray }) => {
        const replacedText = execArray[0];
        const emoji = EMOJI_SHORTCODE_MAP[execArray[1]!];
        if (!emoji) return;

        const baseDocFrom = pos + execArray.index;
        const baseDocTo = baseDocFrom + replacedText.length;
        const from = tr.mapping.map(baseDocFrom);
        const to = tr.mapping.map(baseDocTo);
        this.emojiListController.saveUsedEmoji(emoji.unified);
        tr.replaceRangeWith(from, to, newState.schema.text(emoji.symbol));
      });
      return tr;
    },
  });

  constructor(
    private emojiListController: EmojiListController,
    private scroll$: Observable<void>,
  ) {}

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;

    if (EditorMode.Wysiwyg === mode) {
      this.emojiServiceInternal = new EmojiServiceInternal(
        editorView,
        this.emojiListController,
        this.eventStream$,
        this.scroll$,
        this.destroy$,
      );
      this.api = {
        emojiService: new EmojiServicePublic(editorView, this.emojiServiceInternal),
      };
    }
  }

  public getNativeEditorPlugins() {
    return [this.emojiPlugin, this.emoticonsReplacePlugin];
  }

  public destroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
